// src/pages/CreateWard.jsx
import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../../constant/constant";

import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Snackbar,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TablePagination,
  Box,
  Paper,
  TableContainer,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { decrypt } from "../../../components/cryptoUtil";
import { Heading } from "../../componenet/heading";

const CreateWard = () => {
  let decryptedData = {};
  try {
    const tokenData = localStorage.getItem("tokenData");
    decryptedData = tokenData ? JSON.parse(decrypt(tokenData)) : {};
  } catch (e) {
    console.warn("Failed to decrypt tokenData", e);
    decryptedData = {};
  }

  const token = localStorage.getItem("token") || "";
  const HOSPITAL_ID = decryptedData.softsubgroup || "";

  const [wardName, setWardName] = useState("");
  const [floor, setFloor] = useState("");
  const [classification, setClassification] = useState("");
  const [classifications, setClassifications] = useState([]);
  const [wards, setWards] = useState([]);
  const [newClassification, setNewClassification] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch classifications & wards
  useEffect(() => {
    fetchClassifications();
    fetchWards();
  }, []);

  const fetchClassifications = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/hospital/ward-classifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setClassifications(data);
      }
    } catch (err) {
      console.error("Error fetching classifications:", err);
    }
  };

  const fetchWards = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/hospital/wards/${HOSPITAL_ID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setWards(data);
      }
    } catch (err) {
      console.error("Error fetching wards:", err);
    }
  };

  // Add ward
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!wardName || !floor || !classification) {
      setSnackbar({
        open: true,
        message: "Please fill all fields",
        severity: "error",
      });
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/hospital/wards`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          hospital_id: HOSPITAL_ID,
          ward_name: wardName,
          floor,
          classification_id: classification,
          created_by: decryptedData.userId || "unknown",
        }),
      });

      if (!res.ok) throw new Error("Failed to create ward");
      setSnackbar({
        open: true,
        message: "Ward created successfully",
        severity: "success",
      });
      setWardName("");
      setFloor("");
      setClassification("");
      fetchWards();
    } catch (err) {
      console.error("Error creating ward:", err);
      setSnackbar({
        open: true,
        message: "Error creating ward",
        severity: "error",
      });
    }
  };

  // Add classification
  const handleAddClassification = async () => {
    if (!newClassification) {
      setSnackbar({
        open: true,
        message: "Enter classification name",
        severity: "error",
      });
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/hospital/add_ward-classifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newClassification }),
      });

      if (!res.ok) throw new Error("Failed to add classification");
      setSnackbar({
        open: true,
        message: "Classification added successfully",
        severity: "success",
      });
      setNewClassification("");
      fetchClassifications();
    } catch (err) {
      console.error("Error adding classification:", err);
      setSnackbar({
        open: true,
        message: "Error adding classification",
        severity: "error",
      });
    }
  };

  // Delete ward
  const handleDeleteWard = async (wardId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/hospital/wards/${wardId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete ward");
      setSnackbar({
        open: true,
        message: "Ward deleted successfully",
        severity: "success",
      });
      fetchWards();
    } catch (err) {
      console.error("Error deleting ward:", err);
      setSnackbar({
        open: true,
        message: "Error deleting ward",
        severity: "error",
      });
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Card>
      <CardContent>
         <Heading headingtext="🛏️ Ward Master" />
        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
            <TextField
              label="Ward Name"
              fullWidth
              value={wardName}
              onChange={(e) => setWardName(e.target.value)}
            />
            <TextField
              label="Floor"
              fullWidth
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
            />
            <TextField
              select
              label="Classification"
              fullWidth
              value={classification}
              onChange={(e) => setClassification(e.target.value)}
            >
              {classifications.map((type) => (
                <MenuItem
                  key={type.classification_id}
                  value={type.classification_id}
                >
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <Button type="submit" variant="contained"  sx={{ height: 50, backgroundColor: "brown" ,color:"white"}}>
            🏥 Add Ward
          </Button>
        </form>

        {decryptedData.userrole === 999 && (
          <div style={{ marginTop: "20px", display: "flex", gap: "16px" }}>
            <TextField
              label="New Classification"
              fullWidth
              value={newClassification}
              onChange={(e) => setNewClassification(e.target.value)}
            />
            <Button variant="outlined" onClick={handleAddClassification} sx={{ height: 50, backgroundColor: "brown" ,color:"white"}}>
              ➕ Add Classification
            </Button>
          </div>
        )}

        {/* Table */}
        {wards.length > 0 && (
          <>


          
            {/* <Typography
              variant="h6"
              align="center"
              gutterBottom
              style={{ marginTop: "60px",backgroundColor: "#032038ff", color: "white", padding: "10px", borderRadius: "8px" }}
            >
              📝 Ward List
            </Typography> */}
<TableContainer component={Paper} sx={{ mt: 8, maxHeight: 400, overflowX: "auto" }}>
            <Table sx={{mt:4 }}>
              <TableHead sx={{ backgroundColor: "darkblue",mt:4 }}>
                <TableRow >
                  <TableCell sx={{color:"white",fontSize:14, fontWeight: "bold",textTransform: "uppercase",}}>Ward Name</TableCell>
                  <TableCell sx={{color:"white",fontSize:14, fontWeight: "bold",textTransform: "uppercase",}}>Floor</TableCell>
                  <TableCell sx={{color:"white",fontSize:14, fontWeight: "bold",textTransform: "uppercase",}}>Classification</TableCell>
                   {(decryptedData.userrole === 9 ||
                    decryptedData.userrole === 999) && (
                    <TableCell sx={{color:"white",fontSize:14, fontWeight: "bold",textTransform: "uppercase",}}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {wards
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((ward) => (
                    <TableRow key={ward.ward_id}>
                      <TableCell>{ward.ward_name}</TableCell>
                      <TableCell>{ward.floor}</TableCell>
                      <TableCell>{ward.classification}</TableCell>
                      {(decryptedData.userrole === 9 ||
                        decryptedData.userrole === 999) && (
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteWard(ward.ward_id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
</TableContainer>
            {/* Pagination */}
            <TablePagination
              component="div"
              count={wards.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default CreateWard;
